import { createMuiTheme } from "@material-ui/core/styles";

// These are the only text colours ACCB should use; import them instead of
// writing a new hex.
//
//
//   strong    - page and section headings
//   primary   - body copy
//   secondary - supporting copy that still has to be read
//   muted     - captions, helper text, placeholders, disabled
//   link      - anything clickable that is not a button
//   brand     - the ACCB purple used as a text colour
//   onDark    - text on the purple sidebar / login panel
//   success / error / warning / info - state colours only
export const TEXT = {
  strong: "#1A1A1A",
  primary: "#333333",
  secondary: "#4F4F4F",
  muted: "#757575",
  subtle: "#9E9E9E",
  link: "#4A3BFF",
  brand: "#61386D",
  onDark: "#FFFFFF",
  onDarkMuted: "rgba(255, 255, 255, 0.7)",
  success: "#27AE60",
  error: "#D32F2F",
  warning: "#B7791F",
  info: "#2F80ED",
};
const ACCBTheme = createMuiTheme({
  palette: {
    primary: {
      main: "#61386d",
      light: "#e6c48e",
      dark: "#b8883f",
    },
    secondary: {
      main: "#9e2e2f",
      light: "#b85556",
      dark: "#6d2021",
    },
    common: {
      black: "#000",
      white: "#F7F8FA",
      grey: "#FAFBFF",
      grey1: "#707C97",
      yellow: "#FBD56F",
      red: "#FA8282",
      green: "#33B669",
      lightBlue: "#D3E5EB",
    },
    text: {
      primary: TEXT.primary,
      secondary: TEXT.secondary,
      muted: TEXT.muted,
      link: TEXT.link,
    },
    border: "#E5E5E5",
  },
  typography: {
    fontFamily: '"Noto Sans", sans-serif',
    subtitle1: {},
    subtitle2: {
      textTransform: "capitalize",
    },
    caption: {
      color: TEXT.muted,
      fontSize: "0.75rem",
    },
    overline: {
      fontSize: "0.75rem",
    },
    h1: {
      fontSize: "1.7rem",
      fontWeight: 700,
      lineHeight: "normal",
    },
    h5: {
      fontWeight: "bold",
      fontSize: "2rem",
      lineHeight: "2.1rem",
    },
    h2: {
      fontWeight: "bold",
    },
    body1: {
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: "1rem",
    },
    body2: {
      fontSize: "1.1rem",
    },
    h3: {
      fontSize: "3rem",
      lineHeight: "3rem",
      fontWeight: "bold",
      color: TEXT.secondary,
    },
    h6: {
      color: TEXT.muted,
    },
  },
  shadows: ["none"],
  shape: {
    borderRadius: 0,
  },
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: TEXT.error,
        '&$error': {
          // Keeps it red even when the field is in an error state
          color: TEXT.error,
        },
      },
    },
    MuiCssBaseline: {
      "@global": {
        body: {
          backgroundColor: "#fff",
          fontFamily: '"Noto Sans", sans-serif',
        },
        html: {
          scrollBehavior: "smooth",
        },
        ".MuiFormLabel-asterisk, .MuiInputLabel-asterisk": {
          color: "#ff0000 !important",
        },
      },
    },
    MuiButtonBase: {
      disableRipple: true,
    },
    //
    //   variant="contained" - the one primary action on a screen
    //   variant="outlined"  - secondary actions ("Add Another", "Download PDF")
    //   variant="text"      - ghost/tertiary ("Back", header nav)
    //
    // All three share root's radius, padding, font and size, so they line up
    // when placed side by side. Reach for a variant rather than an inline
    // `style={{ background: ... }}`.
    MuiButton: {
      root: {
        borderRadius: 0,
        textTransform: "capitalize",
        fontFamily: '"Noto Sans", sans-serif',
        fontSize: "1rem",
        padding: "0.75rem 1.5rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
        "&$disabled": {
          backgroundColor: "#EDEDED",
          // Deliberately darker than TEXT.muted: a disabled label still has to
          // clear contrast against the grey fill behind it.
          color: "#848383",
          border: "1px solid #C9C9C9",
        },
      },
      outlined: {
        padding: "0.75rem 1.5rem",
        borderWidth: 1,
        backgroundColor: "#FFFFFF",
        "&:hover": {
          backgroundColor: "#F4EFF6",
        },
      },
      outlinedPrimary: {
        borderColor: "#61386d",
        "&:hover": {
          borderColor: "#61386d",
          backgroundColor: "#F4EFF6",
        },
      },
      text: {
        padding: "0.75rem 1.5rem",
      },
    },
    MuiIconButton: {
      root: {
        padding: "0.75rem",
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 0,
      },
    },
    MuiCheckbox: {
      root: {
        color: "#d3a35c",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 0,
      },
    },
    MuiSelect: {
      select: {
        textAlign: "left",
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: "4px",
        boxShadow:
          "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
        maxHeight: "calc(100% - 200px)",
      },
    },
    MuiMenuItem: {
      root: {
        color: "black",
        "&:hover": {
          backgroundColor: "#d3a35c !important",
          color: TEXT.onDark,
        },
      },
    },
    // Autocomplete renders its own paper/option slots, so the MuiMenu and
    // MuiMenuItem rules above never reach it, while MuiPaper.rounded above
    // squares off its corners. Mirror the menu styling so a searchable
    // dropdown is indistinguishable from a plain Select.
    MuiAutocomplete: {
      paper: {
        borderRadius: "4px",
        boxShadow:
          "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
      },
      option: {
        color: "black",
        // v4 marks the hovered/keyboard-highlighted option with data-focus;
        // there is no :hover rule to override. aria-selected is deliberately
        // left alone — it already uses the same grey as a selected MenuItem.
        '&[data-focus="true"]': {
          backgroundColor: "#d3a35c",
          color: TEXT.onDark,
        },
      },
    },
  },
});

export default ACCBTheme;
